/**
 * EmployeeSkillsPage — the employee-centered Skills lens of one Digital Employee
 * (DIGEMP-SKILL-EMPLOYEE-LENS-FRONTEND-053), at /agents/:id/skills.
 *
 * One screen answering "what can this employee do, how do we know, and what is missing": the
 * registered Skill records (exact version, §28 lifecycle, proficiency level, last evaluation),
 * evidence-based proficiency from the audited run counters, the versioned skill/learning history
 * (each state change with its reason, evidence and provenance), recent usage, and known gaps.
 *
 * Read-only except for ONE governed write, and that write grants nothing: every record comes from an
 * existing, already-permission-gated endpoint under the same agents_view gate as the employee page
 * — GET /api/agents/:id, GET /api/agents/:id/workspace, GET /api/agents/:id/growth-profile and
 * GET /api/agents/:id/experience-history. The Skill-records block reads an additive projection
 * (whichever of the established skill_* carrier keys an agents payload provides) and degrades
 * truthfully while no such projection exists.
 *
 * PROD-SKILLWIRE-017 added the §28.4 lifecycle control inside each record card, for a holder of
 * `agents_manage` only: it performs the status move MASTER_FLOW §3's "GUI-managed Skill Pool" asks
 * for (acceptance criterion 6) through POST /api/skill-lifecycle/{skill_id}/status. It is bounded on
 * three sides — the moves it offers are the SERVER's `available_transitions` (this file holds no
 * transition table), the approver is the signed-in operator and never a field, and a LEVEL cannot
 * move through it at all. So the page still cannot raise a level, grant a tool or assign anything.
 *
 * Truthfulness rules (pinned by tests):
 *  - no source / unreadable payload / empty list are three distinct, explicit states;
 *  - a version, level or timestamp is rendered only when reported as a valid value — never
 *    invented, never rescaled, never rounded into a claim;
 *  - the green "זמין לעבודה" badge requires an active-family status AND a reported level above 0
 *    AND an evaluation signal, and is never given to a self-claimed source (§28.1: a Skill level
 *    grants nothing; §28.5: a level comes only from a human-reviewed evaluation);
 *  - the lifecycle control renders nothing at all when its moves are unknown or unreadable, and
 *    reports the server's own refusal sentence verbatim rather than paraphrasing it;
 *  - tools/permissions are shown as a separate policy domain, never mixed into the Skill list;
 *  - only bounded scalar fields are rendered — no raw payloads, no object dumps, no credentials.
 */
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { agentsApi, skillLifecycleApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import EmployeeAvatar from "../components/EmployeeAvatar";
import SkillManagementPanel from "../components/SkillManagementPanel";
import { SkillVersionHistorySection } from "../components/LearningTimeline";

export const EXPERIENCE_LIMIT = 10;

// §28.2 status vocabulary plus the approved-family alias some projections use.
const ACTIVE_FAMILY = new Set(["active", "approved"]);
const STATUS_META = {
  candidate: { label: "מועמד", cls: "bg-gray-100 text-gray-600 border border-gray-200" },
  active: { label: "פעיל", cls: "bg-green-100 text-green-700 border border-green-200" },
  approved: { label: "מאושר", cls: "bg-green-100 text-green-700 border border-green-200" },
  suspended: { label: "מושהה", cls: "bg-orange-100 text-orange-700 border border-orange-200" },
  deprecated: { label: "הוצא מכלל שימוש", cls: "bg-slate-200 text-slate-700 border border-slate-300" },
};

// §28.5 — a level may come only from a human-reviewed evaluation; these source labels mean the
// record is (or may be) an employee/model self-claim and never earn the availability badge.
export const SELF_CLAIM_SOURCES = new Set([
  "model_claim", "self_claim", "self_reported", "self_report", "self_assessment",
  "agent_claim", "model", "llm", "agent",
]);

const SOURCE_LABELS = {
  training: "מקור: אימון",
  evaluation: "מקור: הערכה",
  manual_grant: "מקור: הקצאה ידנית",
};

// Established additive carrier keys, most-specific first (same family the workspace Skill
// sections read); first found wins.
export const SKILL_LENS_KEYS = [
  "skill_lens", "skills_lens", "employee_skill_lens", "employee_skills",
  "skill_assignment_state", "skill_assignment_status", "assigned_skill_state", "assigned_skills",
  "skill_history", "skills_history", "skill_assignments", "approved_skills",
];
const INNER_LIST_KEYS = ["skills", "approved_skills", "assignments", "skill_assignments", "items", "entries", "records"];

const GAP_STATUS_META = {
  open: { label: "פתוח", cls: "bg-amber-100 text-amber-700 border border-amber-200" },
  acknowledged: { label: "בטיפול", cls: "bg-blue-100 text-blue-700 border border-blue-200" },
};

const OUTCOME_META = {
  verified_success: { label: "מאומת", cls: "bg-green-100 text-green-700" },
  unverified: { label: "טרם אומת", cls: "bg-amber-50 text-amber-700 border border-amber-200" },
  failed: { label: "נכשל", cls: "bg-red-100 text-red-700" },
  cancelled: { label: "נדחה", cls: "bg-slate-200 text-slate-700" },
};

const UNKNOWN_CHIP = "bg-gray-50 text-gray-600 border border-dashed border-gray-300";

function fmt(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString("he-IL");
}

/**
 * Control (C0/C1/DEL), zero-width, bidi and BOM characters are stripped before any display —
 * the same spoofing/obfuscation classes the backend Skill registry refuses. Implemented over
 * code points (no regex literal) so the source file itself carries no invisible characters.
 */
function stripUnsafe(text) {
  let out = "";
  for (const ch of text) {
    const code = ch.codePointAt(0);
    const unsafe =
      code <= 0x1f ||
      (code >= 0x7f && code <= 0x9f) ||
      (code >= 0x200b && code <= 0x200f) ||
      (code >= 0x2028 && code <= 0x202e) ||
      (code >= 0x2060 && code <= 0x206f) ||
      code === 0xfeff;
    if (!unsafe) out += ch;
  }
  return out;
}

/** Bounded display text: strings only, trimmed, capped, per-character sanitized. */
function cleanText(value, max) {
  if (typeof value !== "string") return null;
  const cleaned = stripUnsafe(value).trim();
  if (!cleaned) return null;
  return cleaned.slice(0, max);
}

function cleanInt(value, { min = null, max = null } = {}) {
  if (typeof value !== "number" || !Number.isInteger(value)) return null;
  if (min != null && value < min) return null;
  if (max != null && value > max) return null;
  return value;
}

function pickText(obj, keys, max) {
  for (const key of keys) {
    const text = cleanText(obj?.[key], max);
    if (text) return text;
  }
  return null;
}

function nestedObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}

/**
 * Locate the Skill-records list across carriers (agent detail → workspace → workspace.agent →
 * growth profile). Availability is deliberately multi-valued: "none" (no carrier key at all),
 * "unreadable" (a carrier key whose shape cannot be trusted), "empty" (a list with no records)
 * and "ok" — the UI must not collapse these into one another.
 */
export function normalizeSkillLens(carriers) {
  for (const carrier of carriers) {
    if (!carrier || typeof carrier !== "object") continue;
    for (const key of SKILL_LENS_KEYS) {
      if (!(key in carrier)) continue;
      const value = carrier[key];
      if (value == null) continue;
      if (Array.isArray(value)) return { availability: value.length ? "ok" : "empty", raw: value };
      const inner = nestedObject(value);
      if (inner) {
        for (const innerKey of INNER_LIST_KEYS) {
          if (Array.isArray(inner[innerKey])) {
            return {
              availability: inner[innerKey].length ? "ok" : "empty",
              raw: inner[innerKey],
            };
          }
        }
      }
      return { availability: "unreadable", raw: null };
    }
  }
  return { availability: "none", raw: null };
}

/**
 * One Skill record from a tolerant projection. A record counts only when it carries at least one
 * meaningful field; every other value is reported as null ("לא דווח") — never defaulted.
 */
export function normalizeSkillRecord(raw) {
  if (typeof raw === "string") {
    const name = cleanText(raw, 128);
    return name ? { name, domain: null, subdomain: null, version: null, statusRaw: null, statusKey: null, level: null, source: null, evidenceCount: null, lastEval: null, reviewer: null, evidenceText: null, deprecatedAt: null, scoped: false, skillId: null } : null;
  }
  if (!nestedObject(raw)) return null;
  const lastEvaluation = nestedObject(raw.last_evaluation);
  const record = {
    name: pickText(raw, ["name", "skill_name", "title", "label"], 128),
    domain: pickText(raw, ["domain", "skill_domain", "area"], 96),
    subdomain: pickText(raw, ["subdomain", "skill_subdomain"], 96),
    version: cleanInt(raw.version ?? raw.skill_version, { min: 1 }),
    statusRaw: pickText(raw, ["status", "lifecycle", "lifecycle_status", "state", "skill_status"], 40),
    level: cleanInt(raw.current_level ?? raw.level ?? raw.proficiency, { min: 0, max: 100 }),
    source: pickText(raw, ["source_of_skill", "source", "origin"], 60),
    evidenceCount: cleanInt(raw.evidence_count, { min: 0 }),
    lastEval:
      pickText(raw, ["last_evaluated_at", "last_eval_at", "evaluated_at"], 40) ||
      pickText(lastEvaluation, ["at", "occurred_at", "evaluated_at"], 40),
    reviewer:
      pickText(lastEvaluation, ["approved_by", "reviewer", "approved_by_name"], 80) ||
      pickText(raw, ["approved_by", "reviewed_by", "human_approver", "last_evaluated_by"], 80),
    evidenceText:
      pickText(lastEvaluation, ["evidence", "evidence_summary", "summary"], 200) ||
      pickText(raw, ["evidence_summary"], 200),
    deprecatedAt: pickText(raw, ["deprecated_at"], 40),
    scoped: cleanText(raw.customer_id, 64) != null,
    // The record's own identity, needed by the lifecycle control. Bounded like every other field;
    // null when the projection does not report one, and the control then renders nothing.
    skillId: pickText(raw, ["skill_id", "id"], 64),
  };
  record.statusKey = record.statusRaw ? record.statusRaw.toLowerCase() : null;
  const meaningful = record.name || record.version != null || record.statusKey || record.level != null;
  return meaningful ? record : null;
}

export function hasEvalSignal(record) {
  return Boolean(record.lastEval || record.reviewer);
}

export function isAvailabilityEligible(record) {
  return (
    ACTIVE_FAMILY.has(record.statusKey) &&
    record.level != null &&
    record.level > 0 &&
    hasEvalSignal(record) &&
    !SELF_CLAIM_SOURCES.has(record.source)
  );
}

/** Why this Skill is not routable/available — null when the record has no reported gap. */
export function skillGapReason(record) {
  const status = record.statusKey;
  if (status === "deprecated") return "הוצא מכלל שימוש — אינו זמין לשיבוץ";
  if (status === "suspended") return "מושהה — אינו זמין לשיבוץ";
  if (status === "candidate") return "מועמד — טרם אושר לשיבוץ";
  if (!status) return "סטטוס לא דווח — לא ניתן לאשר זמינות";
  if (!ACTIVE_FAMILY.has(status)) return `סטטוס לא מזוהה (${record.statusRaw}) — לא ניתן לאשר זמינות`;
  if (record.level == null) return "רמת מיומנות לא דווחה";
  if (record.level === 0) return "טרם נמדדה רמה (0)";
  if (!hasEvalSignal(record)) return "חסר תיעוד הערכה אנושית";
  if (SELF_CLAIM_SOURCES.has(record.source)) return "מקור הצהרה עצמית — לא אומת בהערכה";
  return null;
}

function availabilityChip(record) {
  const statusMeta = STATUS_META[record.statusKey];
  const statusLabel = statusMeta ? statusMeta.label : record.statusRaw;
  if (isAvailabilityEligible(record)) {
    return { label: "זמין לעבודה", cls: "bg-green-100 text-green-700 border border-green-200" };
  }
  if (ACTIVE_FAMILY.has(record.statusKey)) {
    const suffix =
      record.level == null || record.level === 0 ? "טרם נמדדה רמה"
      : SELF_CLAIM_SOURCES.has(record.source) ? "מקור הצהרה עצמית — ללא אימות"
      : "חסר תיעוד הערכה";
    return { label: `${statusLabel} · ${suffix}`, cls: "bg-amber-100 text-amber-700 border border-amber-200" };
  }
  if (statusMeta) {
    return { label: `אינו זמין (${statusLabel})`, cls: statusMeta.cls };
  }
  return {
    label: record.statusRaw ? `אינו זמין (סטטוס לא מזוהה: ${record.statusRaw})` : "אינו זמין (סטטוס לא דווח)",
    cls: UNKNOWN_CHIP,
  };
}

function Tile({ label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border shadow-sm p-3 space-y-0.5">
      <div className="text-lg font-bold text-gray-800">{value}</div>
      <div className="text-[11px] text-gray-500">{label}</div>
      {sub && <div className="text-[10px] text-gray-400">{sub}</div>}
    </div>
  );
}

function Chip({ text, cls = "bg-gray-100 text-gray-600 border border-gray-200", title }) {
  return <span title={title} className={`text-[11px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${cls}`}>{text}</span>;
}

// §28.4 status words, for the moves the SERVER offers. A status this map does not carry renders its
// own token rather than nothing, so a server-side vocabulary extension can never produce a blank
// button (and the label is presentation only — the list of moves is never computed here).
const TRANSITION_LABELS = {
  candidate: "החזר למועמד",
  active: "הפעל",
  suspended: "השהה",
  deprecated: "הוצא מכלל שימוש",
};

function transitionLabel(status) {
  return TRANSITION_LABELS[status] || `שנה ל-${status}`;
}

/**
 * The ONE governed write on this page (MASTER_FLOW §3 "GUI-managed Skill Pool", acceptance
 * criterion 6: a supported lifecycle operation performable from the authorized GUI without an
 * application deploy). Three rules it may not break:
 *  - the moves offered are the SERVER's `available_transitions`. This file holds no transition
 *    table, so it can neither offer a move the server would refuse nor hide one it allows, and the
 *    list follows the §28 contract when that contract changes;
 *  - the actor is the session. There is no approver input, and the operator's own identity is the
 *    only principal involved (§28.10);
 *  - a LEVEL is not reachable from here, so the page's other claims about levels still hold.
 * Rendered only for a holder of `agents_manage` — the same permission the endpoint requires (Owner
 * bypasses, Admin is granted, every other role needs an explicit stored grant) — and it renders
 * NOTHING while that permission, the record's id or the moves are unknown: an empty control would
 * read as "this Skill cannot move", which is a different fact from "we could not find out".
 */
function SkillLifecycleControl({ skillId }) {
  const qc = useQueryClient();
  const { can } = useAgentPermissions();
  const canManage = can("agents_manage");
  const [outcome, setOutcome] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["skill-lifecycle", skillId],
    queryFn: () => skillLifecycleApi.state(skillId),
    enabled: canManage && !!skillId,
    retry: false,
    staleTime: 30_000,
  });

  const mutation = useMutation({
    mutationFn: (newStatus) => skillLifecycleApi.changeStatus(skillId, newStatus),
    onSuccess: (result) => {
      setOutcome(result);
      // The chip beside this control is rendered from the employee projections, so they are what
      // must be re-read — otherwise the control would report a move the page still contradicts.
      for (const key of ["agent", "agent-workspace", "agent-growth-profile", "agent-experience-history"]) {
        qc.invalidateQueries({ queryKey: [key] });
      }
      qc.invalidateQueries({ queryKey: ["skill-lifecycle", skillId] });
    },
  });

  if (!canManage || !skillId) return null;

  const transitions = Array.isArray(data?.available_transitions) ? data.available_transitions : null;

  return (
    <div className="border-t border-gray-200 pt-2 space-y-1.5" data-testid={`skill-lifecycle-${skillId}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] font-medium text-gray-500">מחזור חיים (§28.4):</span>
        {isLoading ? (
          <span className="text-[11px] text-gray-400 italic">טוען את הפעולות הזמינות...</span>
        ) : isError ? (
          <span role="alert" className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
            לא ניתן לטעון את הפעולות הזמינות — לא הוצע שינוי
          </span>
        ) : transitions === null ? (
          <span role="alert" className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
            השרת לא דיווח אילו פעולות מותרות — לא הוצע שינוי
          </span>
        ) : transitions.length === 0 ? (
          <span className="text-[11px] text-gray-500">אין פעולה זמינה — מצב זה סופי</span>
        ) : (
          transitions.map((status) => (
            <button
              key={status}
              type="button"
              data-transition={status}
              disabled={mutation.isPending}
              onClick={() => mutation.mutate(status)}
              className="text-[11px] px-2 py-0.5 rounded border border-brand-300 text-brand-700 bg-white hover:bg-brand-50 disabled:opacity-50"
            >
              {transitionLabel(status)}
            </button>
          ))
        )}
      </div>

      {mutation.isPending && <p className="text-[11px] text-gray-400 italic">מבצע את השינוי...</p>}

      {mutation.isError && (
        <p role="alert" className="text-[11px] text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
          {cleanText(mutation.error?.response?.data?.detail, 300) ||
            "הבקשה נכשלה ולא בוצע שינוי."}
        </p>
      )}

      {outcome && !mutation.isPending && (
        <p
          role={outcome.state === "applied" ? "status" : "alert"}
          className={
            outcome.state === "applied"
              ? "text-[11px] text-green-700 bg-green-50 border border-green-200 rounded px-1.5 py-0.5"
              : "text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
          }
        >
          {outcome.state === "applied"
            ? `הסטטוס שונה ל-${outcome.status ?? ""}: `
            : "השינוי נדחה: "}
          {cleanText(outcome.detail, 400) || "—"}
          {outcome.approved_by ? ` (מאושר על ידי ${cleanText(outcome.approved_by, 80)})` : ""}
        </p>
      )}

      <p className="text-[10px] text-gray-400">
        שינוי הסטטוס אינו מעלה רמה ואינו מקנה כלי, הרשאה או אישור. הרמה עולה רק בהערכה אנושית
        מבוקרת, שאינה מתבצעת באף מסך.
      </p>
    </div>
  );
}

function SkillRecordCard({ record }) {
  const availability = availabilityChip(record);
  const statusMeta = STATUS_META[record.statusKey];
  const lastEvalDisplay = fmt(record.lastEval);
  const deprecatedDisplay = fmt(record.deprecatedAt);
  const selfClaim = SELF_CLAIM_SOURCES.has(record.source);
  const levelLabel =
    record.level == null ? "רמת מיומנות: לא דווח"
    : record.level === 0 && !hasEvalSignal(record) ? "רמת מיומנות: טרם נמדדה"
    : `רמת מיומנות: ${record.level}/100`;
  const levelColor =
    record.level == null ? null : record.level >= 70 ? "bg-green-500" : record.level >= 40 ? "bg-amber-500" : "bg-red-500";

  return (
    <li className="rounded-lg border border-gray-100 bg-gray-50/60 p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm font-medium text-gray-800">
          {record.name || <span className="italic text-gray-400">מיומנות ללא שם</span>}
        </span>
        <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${availability.cls}`}>
          {availability.label}
        </span>
        {record.version != null && <Chip text={`גרסה ${record.version}`} cls="bg-violet-50 text-violet-700 border border-violet-200" />}
        {statusMeta ? null : record.statusRaw ? <Chip text={record.statusRaw} cls={UNKNOWN_CHIP} /> : null}
        {record.scoped && <Chip text="בהיקף לקוח" cls="bg-teal-50 text-teal-700 border border-teal-200" title="מיומנות המשויכת ללקוח מסוים" />}
        {record.domain && <Chip text={record.subdomain ? `${record.domain} · ${record.subdomain}` : record.domain} />}
        {record.source && (
          <Chip
            text={SOURCE_LABELS[record.source] || (selfClaim ? "מקור: הצהרה עצמית — ללא אימות" : `מקור: ${record.source}`)}
            cls={selfClaim ? "bg-amber-50 text-amber-800 border border-amber-300" : "bg-gray-100 text-gray-600 border border-gray-200"}
          />
        )}
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
          {record.level != null && (
            <div className={`h-2 rounded-full ${levelColor}`} style={{ width: `${record.level}%` }} />
          )}
        </div>
        <span className="text-xs text-gray-600 whitespace-nowrap">{levelLabel}</span>
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
        {lastEvalDisplay ? (
          <span>
            הערכה אחרונה: {lastEvalDisplay}
            {record.reviewer && <> · הוערך על ידי <span dir="ltr">{record.reviewer}</span></>}
          </span>
        ) : record.reviewer ? (
          <span>הוערך על ידי <span dir="ltr">{record.reviewer}</span> (מועד לא דווח)</span>
        ) : (
          <span className="italic">טרם תועדה הערכה</span>
        )}
        {record.evidenceCount != null && <span>פריטי ראיה: {record.evidenceCount}</span>}
        {deprecatedDisplay && <span>הוצא מכלל שימוש: {deprecatedDisplay}</span>}
      </div>

      {record.evidenceText && (
        <p className="text-xs text-gray-600 bg-white border border-gray-100 rounded px-2 py-1">
          <span className="font-medium text-gray-500">ראיות מההערכה: </span>
          {record.evidenceText}
        </p>
      )}

      <SkillLifecycleControl skillId={record.skillId} />
    </li>
  );
}

function SkillsRegistrySection({ lens, records }) {
  return (
    <section aria-label="מיומנויות רשומות" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <h2 className="text-sm font-semibold text-gray-700">מיומנויות רשומות — גרסה ומחזור חיים</h2>
      <p className="text-[11px] text-gray-400">
        מחזור חיים (§28.4): מועמד ← פעיל/מאושר ← מושהה ← הוצא מכלל שימוש · גרסה עולה רק בשינוי רמה
        שהוערך ואושר על ידי אדם (§28.2/§28.10). מיומנות אינה הרשאה ואינה מקנה גישה.
      </p>

      {lens.availability === "none" && (
        <p className="text-sm text-gray-500">
          לא זוהה מקור מיומנויות רשומות לעובד זה — רשומת המיומנויות (גרסה, מחזור חיים, הערכה
          אחרונה) תוצג כאן כאשר המקור יספק אותה. מדדים מבוססי ראיות מוצגים בהמשך העמוד.
        </p>
      )}
      {lens.availability === "unreadable" && (
        <p role="alert" className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          התקבל מטען מיומנויות שאינו מזוהה — לא ניתן להציג רשומות מיומנות בוודאות.
        </p>
      )}
      {lens.availability === "empty" && (
        <p className="text-sm text-gray-500 italic">טרם נרשמו מיומנויות לעובד זה.</p>
      )}
      {lens.availability === "ok" && (
        <ul className="space-y-2">
          {records.map((record, index) => (
            <SkillRecordCard key={`${record.name || "skill"}-${index}`} record={record} />
          ))}
        </ul>
      )}
    </section>
  );
}

function ProficiencySection({ profile, profileLoading, profileFailed }) {
  const proficiency = profile?.evaluated_proficiency || null;
  return (
    <section aria-label="מיומנות מבוססת ראיות" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-700">מיומנות מבוססת ראיות — שימוש לפי קטגוריה</h2>
        {proficiency?.evaluated_at && (
          <span className="text-[11px] text-gray-400">נמדד: {fmt(proficiency.evaluated_at)}</span>
        )}
      </div>

      {profileFailed ? (
        <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
          לא ניתן לטעון את פרופיל המיומנויות וההערכה
        </p>
      ) : profileLoading || !profile ? (
        <p className="text-sm text-gray-400 italic">טוען את פרופיל המיומנויות...</p>
      ) : proficiency?.has_outcome_evidence ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-brand-600">{Math.round(proficiency.overall_score)}</div>
              <div className="text-[11px] text-gray-400">ציון מוערך כולל</div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center text-xs flex-1 min-w-[16rem]">
              {[
                { label: "שיעור פתרון", value: `${Math.round((proficiency.resolution_rate || 0) * 100)}%` },
                { label: "ריצות שהסתיימו", value: proficiency.evidence?.terminal_runs ?? "—" },
                { label: "נכשלו", value: proficiency.evidence?.failed_runs ?? "—" },
              ].map(({ label, value }) => (
                <div key={label} className="bg-gray-50 rounded-lg py-2">
                  <div className="font-semibold text-gray-700">{value}</div>
                  <div className="text-gray-400 mt-0.5">{label}</div>
                </div>
              ))}
            </div>
          </div>
          {proficiency.by_category?.length > 0 && (
            <div className="space-y-1.5">
              {proficiency.by_category.map((entry) => (
                <div key={entry.category} className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 w-28 truncate" title={entry.category}>{entry.category}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                    <div className="h-1.5 rounded-full bg-brand-400" style={{ width: `${Math.round(entry.completion_rate * 100)}%` }} />
                  </div>
                  <span className="text-xs text-gray-400 w-16 text-left">{entry.completed}/{entry.total}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-400 italic">טרם נמדד — אין עדיין ריצות שהסתיימו להערכה</p>
      )}

      <p className="text-[11px] text-gray-400">
        ההערכה מחושבת ממדדים דטרמיניסטיים של הריצות שהסתיימו (בסיס: agent_run_history) ואינה
        מוצגת כערך לפני שיש ראיות — אין כאן הצהרת מודל.
      </p>
    </section>
  );
}

function RecentUsageSection({ experience, experienceLoading, experienceFailed, agentId }) {
  const entries = useMemo(() => {
    const list = Array.isArray(experience?.entries) ? experience.entries : [];
    return list
      .filter((entry) => entry && typeof entry === "object")
      .slice()
      .sort((a, b) => String(b?.occurred_at || "").localeCompare(String(a?.occurred_at || "")))
      .slice(0, EXPERIENCE_LIMIT);
  }, [experience]);

  return (
    <section aria-label="שימושים אחרונים" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-700">שימושים אחרונים</h2>
        {entries.length > 0 && (
          <span className="text-[11px] text-gray-400">{entries.length} עבודות מתועדות אחרונות</span>
        )}
      </div>

      {experienceFailed ? (
        <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
          לא ניתן לטעון את השימושים האחרונים
        </p>
      ) : experienceLoading ? (
        <p className="text-sm text-gray-400 italic">טוען שימושים אחרונים...</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-gray-400 italic">אין עדיין עבודות מתועדות לעובד זה</p>
      ) : (
        <ul className="space-y-1.5">
          {entries.map((entry) => {
            const outcome = OUTCOME_META[entry.outcome];
            const summary = cleanText(entry.summary, 160) || "עבודה מתועדת";
            const runId = cleanText(entry.provenance?.run_id, 64);
            const ticket = entry.ticket && typeof entry.ticket === "object" ? entry.ticket : null;
            const ticketId = cleanText(ticket?.id, 64);
            const feedbackCount = cleanInt(entry.feedback_count, { min: 0 });
            return (
              <li key={cleanText(entry.entry_id, 128) || runId || summary} className="rounded-lg border border-gray-100 bg-gray-50/60 px-2.5 py-2 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${outcome ? outcome.cls : UNKNOWN_CHIP}`}>
                    {outcome ? outcome.label : cleanText(entry.outcome, 40) || "ללא סטטוס"}
                  </span>
                  {feedbackCount > 0 && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">
                      תוקן על ידי טכנאי ({feedbackCount})
                    </span>
                  )}
                  <span className="text-[11px] text-gray-400 whitespace-nowrap mr-auto">{fmt(cleanText(entry.occurred_at, 40))}</span>
                </div>
                <p className="text-sm text-gray-800">{summary}</p>
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  {ticketId && (
                    <Link to={`/tickets/${ticketId}`} className="text-blue-600 hover:underline">
                      {cleanInt(ticket?.ticket_number, { min: 1 }) != null ? `קריאה #${ticket.ticket_number}` : "קריאה מקושרת"}
                    </Link>
                  )}
                  {runId && (
                    <Link
                      to={`/agent-runs/${runId}`}
                      className="text-blue-600 hover:underline"
                      aria-label={`צפה בריצה של ${summary.slice(0, 40)}`}
                    >
                      צפה בריצה
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-400">
        <span>מבוסס על רצף העבודות המבוקר של העובד — קריאה בלבד.</span>
        <Link to={`/agents/${agentId}/learning-center`} className="text-brand-600 hover:underline">
          לרצף הלמידה המלא ←
        </Link>
      </div>
    </section>
  );
}

function GapsSection({ profile, profileFailed, lens, records }) {
  const goals = Array.isArray(profile?.learning_goals) ? profile.learning_goals : [];
  const skillGaps = records
    .map((record) => ({ record, reason: skillGapReason(record) }))
    .filter((row) => row.reason);

  return (
    <section aria-label="פערי ידע ומה חסר" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <h2 className="text-sm font-semibold text-gray-700">פערי ידע ומה חסר</h2>

      {profileFailed ? (
        <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
          לא ניתן לטעון את פערי הידע
        </p>
      ) : goals.length === 0 ? (
        <p className="text-sm text-gray-400 italic">אין פערי ידע פתוחים המשויכים לעובד</p>
      ) : (
        <ul className="space-y-1.5">
          {goals.map((goal) => {
            const goalStatus = typeof goal?.status === "string" ? goal.status.toLowerCase() : null;
            const statusMeta = GAP_STATUS_META[goalStatus];
            const topic = cleanText(goal?.topic, 240);
            const category = cleanText(goal?.category, 120);
            const occurrence = cleanInt(goal?.occurrence_count, { min: 1 });
            const sourceRuns = cleanInt(goal?.source_run_count, { min: 0 });
            return (
              <li key={cleanText(goal?.goal_id, 128) || topic} className="rounded-lg border border-gray-100 bg-gray-50/60 px-2.5 py-2 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${statusMeta ? statusMeta.cls : UNKNOWN_CHIP}`}>
                    {statusMeta ? statusMeta.label : goalStatus || "סטטוס לא דווח"}
                  </span>
                  {category && <Chip text={category} />}
                  {occurrence != null && <span className="text-[11px] text-gray-400">נצפה {occurrence} פעמים</span>}
                  {sourceRuns != null && <span className="text-[11px] text-gray-400">מבוסס על {sourceRuns} ריצות</span>}
                  <span className="text-[11px] text-gray-400 whitespace-nowrap mr-auto">{fmt(cleanText(goal?.last_seen_at, 40))}</span>
                </div>
                <p className="text-sm text-gray-800">{topic || "פער ידע ללא תקציר"}</p>
              </li>
            );
          })}
        </ul>
      )}

      <div className="space-y-1.5">
        <p className="text-xs text-gray-500 font-medium">מיומנויות שאינן זמינות לשיבוץ</p>
        {lens.availability === "none" && (
          <p className="text-[11px] text-gray-400">
            ללא רשומת מיומנויות — לא ניתן להסיק פערי מיומנות; פערי הידע שלמעלה מבוססים על ריצות העובד בלבד.
          </p>
        )}
        {lens.availability === "ok" && skillGaps.length === 0 && (
          <p className="text-[11px] text-gray-400">כל המיומנויות הרשומות עומדות בתנאי הזמינות המדווחים.</p>
        )}
        {skillGaps.length > 0 && (
          <ul className="space-y-1">
            {skillGaps.map(({ record, reason }, index) => (
              <li key={`${record.name || "skill"}-${index}`} className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                <span className="font-medium">{record.name || "מיומנות ללא שם"}</span>
                <span className="text-gray-500">— {reason}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function PermissionsSeparationSection({ workspace, workspaceFailed, agentId }) {
  const tools = Array.isArray(workspace?.capabilities?.tools) ? workspace.capabilities.tools : null;
  return (
    <section aria-label="מיומנות מול הרשאות" className="bg-white rounded-xl border shadow-sm p-4 space-y-2">
      <h2 className="text-sm font-semibold text-gray-700">מיומנות אינה הרשאה</h2>
      <p className="text-sm text-gray-600">
        מיומנות ורמת מיומנות אינן מקנות הרשאה, וכלי מורשה אינו הוכחה למיומנות. הרשאות וכלים הם
        מדיניות גישה נפרדת — הם מנוהלים בעמוד העובד ובעמוד ההרשאות, ולא במסך זה. המסך אינו מעניק
        ואינו משנה הרשאות.
      </p>
      <p className="text-xs text-gray-500">
        כלים מורשים לעובד זה:{" "}
        {workspaceFailed ? (
          <span className="text-amber-700">— (מקור ההרשאות לא נטען)</span>
        ) : tools ? (
          <span className="font-medium">{tools.length}</span>
        ) : workspace ? (
          <span className="text-gray-500">— (לא דווח)</span>
        ) : (
          <span className="text-gray-400">…</span>
        )}
        {" · "}
        <Link to={`/agents/${agentId}`} className="text-brand-600 hover:underline">
          לפירוט ההרשאות בעמוד העובד ←
        </Link>
      </p>
    </section>
  );
}

export default function EmployeeSkillsPage() {
  const { id } = useParams();

  const { data: agent, isLoading: agentLoading, error: agentError } = useQuery({
    queryKey: ["agent", id],
    queryFn: () => agentsApi.get(id),
    enabled: !!id,
  });

  const { data: workspace, isLoading: workspaceLoading, isError: workspaceFailed } = useQuery({
    queryKey: ["agent-workspace", id],
    queryFn: () => agentsApi.workspace(id),
    enabled: !!id,
    staleTime: 60_000,
  });

  const { data: profile, isLoading: profileLoading, isError: profileFailed } = useQuery({
    queryKey: ["agent-growth-profile", id],
    queryFn: () => agentsApi.growthProfile(id),
    enabled: !!id,
    staleTime: 60_000,
  });

  const { data: experience, isLoading: experienceLoading, isError: experienceFailed } = useQuery({
    queryKey: ["agent-experience-history", id],
    queryFn: () => agentsApi.experienceHistory(id, { limit: EXPERIENCE_LIMIT }),
    enabled: !!id,
    staleTime: 60_000,
  });

  const lens = useMemo(
    () => normalizeSkillLens([agent, workspace, workspace?.agent, profile]),
    [agent, workspace, profile],
  );
  const records = useMemo(
    () => (lens.availability === "ok" ? lens.raw.map(normalizeSkillRecord).filter(Boolean) : []),
    [lens],
  );

  if (agentLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (!agent) {
    const refused = agentError?.response?.status === 403;
    return (
      <div className="text-center py-16" dir="rtl">
        <p className="text-red-600 text-sm">
          {refused ? "אין הרשאה לצפות בעובד זה" : "לא ניתן לטעון את העובד"}
        </p>
        <Link to="/agents" className="text-xs text-brand-600 hover:underline mt-2 block">← חזרה לסוכנים</Link>
      </div>
    );
  }

  const proficiency = profile?.evaluated_proficiency || null;
  const goals = Array.isArray(profile?.learning_goals) ? profile.learning_goals : [];
  const eligibleCount = records.filter((record) => skillGapReason(record) === null).length;
  const registryTile = (() => {
    if (agentLoading || workspaceLoading) return { value: "…", sub: "" };
    if (lens.availability === "ok") return { value: records.length, sub: "מקור: רשומת המיומנויות של העובד" };
    if (lens.availability === "empty") return { value: 0, sub: "טרם נרשמו מיומנויות" };
    if (lens.availability === "unreadable") return { value: "—", sub: "מטען מיומנויות לא מזוהה" };
    return { value: "—", sub: "מקור המיומנויות טרם זמין לעובד זה" };
  })();

  return (
    <div className="space-y-5 max-w-5xl mx-auto" dir="rtl">
      <div>
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
          <Link to="/agents" className="hover:text-gray-600">סוכנים</Link>
          <span>/</span>
          <Link to={`/agents/${id}`} className="hover:text-gray-600">{agent.name}</Link>
          <span>/</span>
          <span className="text-gray-600">מיומנויות העובד</span>
        </div>
        <div className="flex items-center gap-3">
          <EmployeeAvatar name={agent.name} avatarKey={agent.avatar_key} avatarColor={agent.avatar_color} size="lg" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">🎯 מיומנויות העובד — {agent.name}</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              מיומנויות רשומות עם גרסה ומחזור חיים, הערכה אחרונה, מיומנות מבוססת ראיות, היסטוריית
              גרסאות מיומנות, שימושים אחרונים ופערי ידע — הכול ממקורות מתועדים בלבד. מיומנות אינה
              הרשאה, והמסך אינו מעניק הרשאות; פעולות ההקצאה וההסרה משנות שיוך בלבד ואינן מעלות רמה,
              אינן מקנות כלי או אישור ואינן מוחקות רשומה.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="מיומנויות רשומות" value={registryTile.value} sub={registryTile.sub} />
        <Tile
          label="זמינים לעבודה"
          value={lens.availability === "ok" ? eligibleCount : "—"}
          sub={lens.availability === "ok" ? `מתוך ${records.length} רשומות מדווחות` : "נדרשת רשומת מיומנויות"}
        />
        <Tile
          label="ציון מוערך"
          value={
            profileFailed ? "—"
            : proficiency?.has_outcome_evidence ? Math.round(proficiency.overall_score)
            : profile ? "טרם נמדד" : "…"
          }
          sub="מבוסס ריצות שהסתיימו"
        />
        <Tile
          label="פערי ידע"
          value={profileFailed ? "—" : profile ? goals.length : "…"}
          sub="מקושרים לריצות של העובד"
        />
      </div>

      <SkillsRegistrySection lens={lens} records={records} />

      <SkillManagementPanel agentId={id} records={records} />

      <ProficiencySection profile={profile} profileLoading={profileLoading} profileFailed={profileFailed} />

      <SkillVersionHistorySection
        history={agent.learning_history}
        isLoading={agentLoading}
        isError={!!agentError}
      />

      <RecentUsageSection
        experience={experience}
        experienceLoading={experienceLoading}
        experienceFailed={experienceFailed}
        agentId={id}
      />

      <GapsSection profile={profile} profileFailed={profileFailed} lens={lens} records={records} />

      <PermissionsSeparationSection workspace={workspace} workspaceFailed={workspaceFailed} agentId={id} />

      <div className="text-[11px] text-gray-400 space-y-0.5">
        {profile?.generated_at && <div>פרופיל מיומנויות: נטען {fmt(profile.generated_at)}</div>}
        {experience?.generated_at && (
          <div>רצף עבודות: נטען {fmt(experience.generated_at)} (עד {EXPERIENCE_LIMIT} עבודות אחרונות)</div>
        )}
        <div>
          כל הרשומות מגיעות מהרשומות המבוקרות הקיימות במערכת — ללא זיכרון מודל, ללא מטעני גלם
          וללא סודות. טקסטים מוצגים קטועים ומוגבלים באורכם.
        </div>
      </div>

      <div>
        <Link to={`/agents/${id}`} className="text-xs text-brand-600 hover:underline">← חזרה לעמוד העובד</Link>
      </div>
    </div>
  );
}
